/* eslint-disable max-len */
import map from 'lodash/map';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import omit from 'lodash/omit';
import forEach from 'lodash/forEach';
import size from 'lodash/size';

import employerActions from '../../entities/employers/actions';
import grantsAPI from '../../../../utils/api/grantsAPI';
import logger from '../../../../utils/logger';
import { hasJobcaseEmployerAdminRole } from '../../../../utils/grants';
// eslint-disable-next-line import/no-cycle
import { getOrganization } from '../settings/actions';
import { getSubOrganization } from '../../entities/subOrganizations/actions';

const TYPES = {
  GET_EMPLOYER_PROFILE_REQUEST: 'employerProfile/GET_EMPLOYER_PROFILE_REQUEST',
  GET_EMPLOYER_PROFILE_SUCCESS: 'employerProfile/GET_EMPLOYER_PROFILE_SUCCESS',
  GET_EMPLOYER_PROFILE_ERROR: 'employerProfile/GET_EMPLOYER_PROFILE_ERROR',
  SET_IS_EMPLOYER_PROFILE: 'employerProfile/SET_IS_EMPLOYER_PROFILE',
  SET_HAS_MULTIPLE_EMPLOYERS: 'employerProfile/SET_HAS_MULTIPLE_EMPLOYERS',
  SET_EMPLOYER_GRANTS: 'employerProfile/SET_EMPLOYER_GRANTS',
  SET_EMPLOYER_GRANTS_BY_ID: 'employerProfile/SET_EMPLOYER_GRANTS_BY_ID',
  SET_IS_JOBCASE_EMPLOYER_ADMIN: 'SET_IS_JOBCASE_EMPLOYER_ADMIN',
};

const setIsEmployerProfile = isEmployerProfile => ({
  type: TYPES.SET_IS_EMPLOYER_PROFILE,
  payload: isEmployerProfile,
});

const OMITTED_GRANT_KEYS = [
  'employer_id',
  'profile_id',
];

const setEmployerGrants = (grants) => {
  const grantsGroupedByEmployer = groupBy(grants, grant => grant.employer_id);
  const filteredGrants = {};
  forEach(grantsGroupedByEmployer, (employerGrants, key) => {
    filteredGrants[key] = map(employerGrants, grant => omit(grant, OMITTED_GRANT_KEYS));
  });
  return {
    type: TYPES.SET_EMPLOYER_GRANTS,
    payload: filteredGrants,
  };
};

const setEmployerGrantsById = (employerKey, grants) => ({
  type: TYPES.SET_EMPLOYER_GRANTS_BY_ID,
  payload: { employerKey, grants },
});

const setHasMultipleEmployers = hasMultipleEmployers => ({
  type: TYPES.SET_HAS_MULTIPLE_EMPLOYERS,
  payload: hasMultipleEmployers,
});

const setCurrentEmployer = (employerId, subEmployerId = null) => ({
  type: TYPES.GET_EMPLOYER_PROFILE_SUCCESS,
  payload: {
    employerId,
    subEmployerId,
  },
});

const loadEmployerAccount = (employerId, ctx = {}) => async (dispatch) => {
  try {
    const response = await dispatch(employerActions.get(employerId, { fetch: { ctx } }));
    if (response?.data) {
      await dispatch(getOrganization({ organizationId: employerId, ctx }));
      return response?.data;
    }
    return null;
  } catch (error) {
    return null;
  }
};

const loadAllEmployerAccounts = (employerId = '', subEmployerId = '', ctx) => async (dispatch) => {
  try {
    const { data: profileGrants } = await grantsAPI().getProfileGrants(ctx);
    const { grant_map: grantMap } = profileGrants;

    const transformedProfileGrants = Object.entries(grantMap).flatMap(([employerId, grants]) => Object.values(grants).flatMap(grantList => grantList.map(grant => ({
      employer_id: employerId,
      profile_id: profileGrants.profile_id,
      role: grant.role,
      level: grant.level,
    }))));

    dispatch(setEmployerGrants(transformedProfileGrants));
    const jobcaseEmployerAdminLevel = hasJobcaseEmployerAdminRole(transformedProfileGrants);
    if (jobcaseEmployerAdminLevel) {
      dispatch({ type: TYPES.SET_IS_JOBCASE_EMPLOYER_ADMIN, payload: jobcaseEmployerAdminLevel });
    }

    if (isEmpty(transformedProfileGrants)) {
      dispatch(setIsEmployerProfile(false));
      return;
    }
    dispatch(setIsEmployerProfile(true));

    const employerIds = uniq(map(transformedProfileGrants, grant => grant.employer_id));
    dispatch(setHasMultipleEmployers(size(employerIds) > 1));

    const employersPromises = employerIds.map(id => new Promise(async (resolve, reject) => {
      try {
        const response = await dispatch(employerActions.get(id, { fetch: { ctx } }));
        resolve(response);
      } catch (error) {
        reject(error);
      }
    }));
    const employerAccounts = await Promise.all(employersPromises);

    let currentEmployerId = null;
    if (employerId) {
      currentEmployerId = employerId;
    } else {
      currentEmployerId = employerAccounts[0]?.data?.id;
    }

    if (subEmployerId) {
      await dispatch(getSubOrganization({
        orgId: currentEmployerId,
        subOrgId: subEmployerId,
        ctx,
      }));
    }

    const organizationPromises = employerIds.map(id => new Promise(async (resolve, reject) => {
      try {
        await dispatch(getOrganization({ organizationId: id, ctx }));
        resolve();
      } catch (error) {
        reject(error);
      }
    }));
    await Promise.allSettled(organizationPromises);

    dispatch(setCurrentEmployer(currentEmployerId));
  } catch (error) {
    logger.logDebug('Encountered error while loading employer accounts', error);
    dispatch({ type: TYPES.GET_EMPLOYER_PROFILE_ERROR, payload: error });
  }
};

export {
  TYPES,
  loadAllEmployerAccounts,
  setCurrentEmployer,
  setEmployerGrants,
  setEmployerGrantsById,
  loadEmployerAccount,
};

export default {
  TYPES,
  loadAllEmployerAccounts,
  setCurrentEmployer,
  setEmployerGrants,
  setEmployerGrantsById,
  loadEmployerAccount,
};
